import React, { useEffect, useState } from "react";
import { get } from "lodash";
import { Button, CircularProgress, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs } from "@mui/material";
import { styled } from "@mui/material/styles";
import BSInput from "../../../common/js/components/BSInput";
import { Link } from "react-router-dom";
import operations from "./ducks/operations";
import schema from "./DocumentAdminLandingTable";
import { Search, Sorty } from "../../../common/js/icons";

const PageToolbar = styled("div")(() => ({
    marginBottom: "20px",
    marginTop: "20px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
}));

const TabOne = styled(Tab)(() => ({
    marginRight: "20px",
}));

const Sortable = styled("span")(() => ({
    paddingRight: "5px",
    paddingBottom: "3px",
}));

const LoadingSpinner = styled("div")(() => ({
    position: "absolute",
    left: "50%",
    top: "45%",
    transform: "translate(-50%, -50%)",
    marginTop: "50px",
}));

const DocumentLink = styled(Link)(() => ({
    textDecoration: "none",
}));

const ListingIdLink = styled("span")(() => ({
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
    color: "#2C99D2",
    fontWeight: 600,
}));

const DocumentAdminLanding = () => {
    const [tab, setTab] = useState("documents");
    const [viewHeaders, setViewHeaders] = useState("documents");
    const [documentTypes, setDocumentTypes] = useState([]);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [sortMap, setSortMap] = useState(new Map());
    const [searchInput, setSearchInput] = useState("");
    const [parentTypes, setParentTypes] = useState([]);

    const onKeyPress = async (event) => {
        if (event.key === "Enter") {
            setDataLoaded(false);
            if (tab === "documents") {
                const allDocumentsTypes = await operations.getAllDocumentTypes();
                setDocumentTypes(allDocumentsTypes);
                if (searchInput) {
                    let condition = new RegExp(searchInput.toLowerCase());
                    let newData = documentTypes.filter(function (el) {
                        return condition.test(el.name.toLowerCase());
                    });
                    setDocumentTypes(newData);
                }
            }
            setDataLoaded(true);
        }
    };

    const handleSort = (header) => {
        const isDocumentsTab = tab === "documents";
        const listToSort = isDocumentsTab ? documentTypes : parentTypes;

        let newData = [...listToSort].sort((a, b) => {
            if (header === "parentEntityType") {
                return ("" + a.parentEntityType.name).localeCompare(b.parentEntityType.name);
            }
            return ("" + a[header]).localeCompare(b[header]);
        });

        const newSortMap = new Map(sortMap);
        if (sortMap.get(header)) {
            newData.reverse();
            newSortMap.set(header, false);
        } else {
            newSortMap.set(header, true);
        }

        setSortMap(newSortMap);

        if (isDocumentsTab) {
            setDocumentTypes(newData);
        } else {
            setParentTypes(newData);
        }
    };

    const loadData = async () => {
        const docTypes = await operations.getAllDocumentTypes();
        const sorted = [...docTypes].sort((a, b) => ("" + a.name).localeCompare(b.name));
        setDocumentTypes(sorted);
        setDataLoaded(true);
    };

    const switchTab = async (event, value) => {
        setViewHeaders(value);
        setTab(value);

        if (value === "parentTypes" && Object.keys(parentTypes).length === 0) {
            setDataLoaded(false);
            const currentParentTypes = await operations.getAllParentEntityTypes();
            setParentTypes(currentParentTypes);
        }
        setDataLoaded(true);
    };

    useEffect(() => {
        loadData();
    }, []);

    return (
        <div>
            <PageToolbar>
                <div>
                    <h2 className="page-title">Document Admin</h2>
                </div>
                {tab === "parentTypes" ? null : (
                    <Button disabled={false} variant="contained" color="primary" href="/document-type/create">
                        + Add Document
                    </Button>
                )}
            </PageToolbar>
            <div className="simPaper">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                        <Tabs value={tab} onChange={switchTab}>
                            <TabOne value="documents" label="Documents" />
                            <Tab value="parentTypes" label="Parent Types" />
                        </Tabs>
                    </div>
                    <div>
                        {tab === "parentTypes" ? null : (
                            <BSInput
                                value={searchInput}
                                placeholder="Search Documents"
                                errorPlaceholder="hideIt"
                                startAdornment={<Search />}
                                onChange={(event) => {
                                    setSearchInput(event.target.value);
                                }}
                                onKeyPress={(e) => onKeyPress(e)}
                            />
                        )}
                    </div>
                </div>
                <div>
                    <div style={{ minHeight: "60vh" }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {schema.viewHeaders[viewHeaders].map((header, i) => (
                                        <TableCell key={`thc-${i}`}>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                }}
                                                onClick={() => handleSort(header.prop)}
                                            >
                                                <Sortable>{header.name}</Sortable>

                                                {header.name && (
                                                    <div>
                                                        <Sorty />
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            {dataLoaded ? (
                                <TableBody>
                                    {tab === "documents"
                                        ? Object.keys(documentTypes).map((documentType, index) => {
                                              return row(
                                                  documentTypes[documentType],
                                                  index,
                                                  viewHeaders,
                                                  "/document-type/document/" +
                                                      documentTypes[documentType].documentTypeKey
                                              );
                                          })
                                        : parentTypes.map((parentType, index) => {
                                              return row(
                                                  parentType,
                                                  index,
                                                  viewHeaders,
                                                  "/parent-type/" + parentType.parentEntityTypeKey
                                              );
                                          })}
                                </TableBody>
                            ) : (
                                <LoadingSpinner>
                                    <CircularProgress color="primary" />
                                </LoadingSpinner>
                            )}
                        </Table>
                    </div>
                </div>
            </div>
        </div>
    );
};

const row = (element, index, viewHeader, link) => {
    return (
        <TableRow key={`tr-${index}`} selectable="false">
            {schema.viewHeaders[viewHeader].map((header, i) => (
                <TableCell key={`trc-${i}`}>
                    {header.name === "NAME" ? (
                        <DocumentLink to={link}>
                            <ListingIdLink>{get(element, header.prop, "")}</ListingIdLink>
                        </DocumentLink>
                    ) : header.prop === "parentEntityType" ? (
                        get(element, header.prop, "").name
                    ) : (
                        get(element, header.prop, "")
                    )}
                </TableCell>
            ))}
        </TableRow>
    );
};

export default DocumentAdminLanding;
